/**
 * Spring physics animation for Steve Render Service.
 *
 * Simulates a damped harmonic oscillator to produce natural
 * spring-like motion. Standard physics equations — no proprietary code.
 *
 * Written from scratch for Steve.AI.
 */

const DEFAULT_CONFIG = {
  damping: 10,
  mass: 1,
  stiffness: 100,
  overshootClamping: false,
};

// Cache to avoid redundant computation
const calcCache = {};

/**
 * Advance the spring simulation by one time step.
 */
function step(state, time, config) {
  const dt = Math.min(time - state.lastTime, 64);
  const c = config.damping;
  const m = config.mass;
  const k = config.stiffness;

  const v0 = -state.velocity;
  const x0 = state.target - state.position;

  const zeta = c / (2 * Math.sqrt(k * m)); // damping ratio
  const omega0 = Math.sqrt(k / m); // natural frequency
  const omega1 = omega0 * Math.sqrt(1 - zeta ** 2); // damped frequency

  const t = dt / 1000;

  let newPosition, newVelocity;

  if (zeta < 1) {
    // Under-damped
    const envelope = Math.exp(-zeta * omega0 * t);
    const sin1 = Math.sin(omega1 * t);
    const cos1 = Math.cos(omega1 * t);
    const frag = envelope * (sin1 * ((v0 + zeta * omega0 * x0) / omega1) + x0 * cos1);
    newPosition = state.target - frag;
    newVelocity =
      zeta * omega0 * frag -
      envelope * (cos1 * (v0 + zeta * omega0 * x0) - omega1 * x0 * sin1);
  } else {
    // Critically damped (or over-damped, treated as critical)
    const envelope = Math.exp(-omega0 * t);
    newPosition = state.target - envelope * (x0 + (v0 + omega0 * x0) * t);
    newVelocity = envelope * (v0 * (t * omega0 - 1) + t * x0 * omega0 * omega0);
  }

  return {
    position: newPosition,
    velocity: newVelocity,
    target: state.target,
    lastTime: time,
  };
}

/**
 * Calculate spring state at a given frame.
 */
export function springCalculation({ from = 0, to = 1, frame, fps, config = {} }) {
  const cacheKey = `${from}:${to}:${frame}:${fps}:${config.damping}:${config.mass}:${config.stiffness}:${config.overshootClamping}`;
  if (calcCache[cacheKey]) return calcCache[cacheKey];

  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  let state = { position: from, velocity: 0, target: to, lastTime: 0 };

  const frameClamped = Math.max(0, frame);
  const wholeFrames = Math.floor(frameClamped);
  const remainder = frameClamped % 1;

  for (let f = 0; f <= wholeFrames; f++) {
    const actualFrame = f === wholeFrames ? f + remainder : f;
    const time = (actualFrame / fps) * 1000;
    state = step(state, time, mergedConfig);
  }

  const result = { current: state.position, velocity: state.velocity, toValue: to };
  calcCache[cacheKey] = result;
  return result;
}

/**
 * Measure how many frames it takes for the spring to settle.
 */
export function measureSpring({ fps, config = {}, threshold = 0.005, from = 0, to = 1 }) {
  if (threshold <= 0 || threshold >= 1) return threshold === 1 ? 0 : Infinity;

  const range = Math.abs(from - to);
  let frame = 0;
  let settledAt = 0;

  for (let i = 0; i < 10000; i++) {
    frame++;
    const result = springCalculation({ fps, frame, config, from, to });
    const diff = Math.abs(result.current - to) / (range || 1);

    if (diff >= threshold) {
      settledAt = frame + 1;
    }

    // Check 20 frames of stability after settling
    if (frame - settledAt > 20 && diff < threshold) {
      return settledAt;
    }
  }

  return settledAt;
}

/**
 * Main spring function — returns the animated value at a frame.
 *
 * @param {object} params
 * @param {number} params.frame - Current frame
 * @param {number} params.fps - Frames per second
 * @param {object} [params.config] - { damping, mass, stiffness, overshootClamping }
 * @param {number} [params.from=0]
 * @param {number} [params.to=1]
 * @param {number} [params.durationInFrames] - Stretch/compress to fixed duration
 * @param {number} [params.durationRestThreshold]
 * @param {number} [params.delay=0] - Frames to wait before starting
 * @param {boolean} [params.reverse=false]
 * @returns {number}
 */
export function spring({
  frame: passedFrame,
  fps,
  config = {},
  from = 0,
  to = 1,
  durationInFrames: passedDuration,
  durationRestThreshold,
  delay = 0,
  reverse = false,
}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const needsNaturalDuration = reverse || passedDuration !== undefined;
  const naturalDuration = needsNaturalDuration
    ? measureSpring({ fps, config: mergedConfig, from, to, threshold: durationRestThreshold })
    : undefined;

  let frame = passedFrame - delay;
  if (reverse && naturalDuration !== undefined) {
    frame = (passedDuration ?? naturalDuration) - passedFrame;
  }

  const effectiveFrame =
    passedDuration === undefined || naturalDuration === undefined
      ? frame
      : frame / (passedDuration / naturalDuration);

  const result = springCalculation({ fps, frame: effectiveFrame, config: mergedConfig, from, to });

  if (!mergedConfig.overshootClamping) {
    return result.current;
  }

  // Clamp if overshoot clamping is enabled
  if (to >= from) {
    return Math.min(result.current, to);
  }
  return Math.max(result.current, to);
}
